import { nextTick } from "vue";
import { t } from "@/i18n";

import { iframeMutations } from "@/shared/store/iframe.mutations";
import { copy, parseHtml } from "@/shared/utils/string";
import { waitForMe, cloneDeep } from "@/shared/utils/helpers";
import { cookiesService } from "../services/cookies.service";
import { projectsService } from "@/shared/projects/projects.service";
import { prepareCookie } from "@/shared/projects/prepareData";
import { cookieSnapshotService } from "@/apps/cookie/store/cookie-snapshot.service";
import { makeLogger } from "@/shared/consola";

const logger = makeLogger("cookie.store");

export default {
    namespaced: true,
    state: () => ({
        cookies: [],
        cookie: {},
        listWebhook: "https://hook.eu1.make.com/jwg0d73uo03ganwswe43bchsz7ymfbku",
        consentTypes: [
            "strict",
            "ad",
            "aduser",
            "adpersonalization",
            "analytics",
            "functionality",
            "personalization",
            "security",
        ],
        consentTypesGhost: {}, // Not needed for state (just for snapshot logic)
        iframe: null,
        iframeLoaded: false,
        hasChanges: false,
        hasSnapshot: false,
    }),
    getters: {
        appUrl: (s, g, rootS, rootGetters) => rootGetters.easyUrls.cookie,
        defaultTypes: () => {
            return {
                strict: {
                    name: t("cookie.groups.strict_name"),
                    info: t("cookie.groups.strict_info"),
                },
                ad: {
                    name: t("cookie.groups.ad_name"),
                    info: t("cookie.groups.ad_info"),
                },
                aduser: {
                    name: t("cookie.groups.aduser_name"),
                    info: t("cookie.groups.aduser_info"),
                },
                adpersonalization: {
                    name: t("cookie.groups.adpersonalization_name"),
                    info: t("cookie.groups.adpersonalization_info"),
                },
                analytics: {
                    name: t("cookie.groups.analytics_name"),
                    info: t("cookie.groups.analytics_info"),
                },
                functionality: {
                    name: t("cookie.groups.functionality_name"),
                    info: t("cookie.groups.functionality_info"),
                },
                personalization: {
                    name: t("cookie.groups.personalization_name"),
                    info: t("cookie.groups.personalization_info"),
                },
                security: {
                    name: t("cookie.groups.security_name"),
                    info: t("cookie.groups.security_info"),
                },
            };
        },
    },
    mutations: {
        ...iframeMutations,
        setProject(state, { cookie, snapshot = {} }) {
            logger.log("Making snapshot with: ", cookie);
            state.cookie = cookieSnapshotService.makeSnapshotState(
                state,
                "cookie",
                cookie,
                snapshot
            );
            state.cookie.consentTypes = cookieSnapshotService.makeSnapshotState(
                state,
                "consentTypesGhost",
                state.cookie.consentTypes || {},
                snapshot
            );
            state.hasChanges = false;
        },
        clearProject(state) {
            state.cookie = {};
            state.consentTypesGroup = {};
            cookieSnapshotService.clearSnapshot(state, "cookie", {});
            cookieSnapshotService.clearSnapshot(state, "consentTypesGhost", {});
        },
    },
    actions: {
        refreshIframe({ state, getters }) {
            const cookie = cloneDeep(state.cookie);
            state.iframe = document.querySelector("#iframe");
            if (state.iframe && cookie.id) {
                state.iframe.contentWindow.postMessage({ cookieData: cookie }, getters.appUrl);
            }
        },
        async saveSnapshot({ rootState, state, dispatch }) {
            rootState.waiting = true;
            try {
                await cookieSnapshotService.updateSnapshotAfterSave(state.cookie.uuid, {
                    stateSlice: state,
                    actionToTakeIfNotRequired: () => dispatch("mergeSnapshotChanges"),
                });
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async mergeSnapshotChanges({ state, rootState, commit }) {
            await nextTick();

            rootState.waiting = true;

            const cookie = cloneDeep(state.cookie);
            delete cookie.id;
            delete cookie.createdAt;
            delete cookie.updatedAt;
            delete cookie.publishedAt;

            try {
                const updatedCookie = await projectsService.update({
                    tool: "cookie",
                    uuid: state.cookie.uuid,
                    data: cookie,
                });

                state.hasSnapshot = false;

                commit("showNotify", "autoSave", { root: true });
                await nextTick();
                const cookie2 = prepareCookie(updatedCookie);
                const { consentTypes } = cookie2;
                commit("setProject", { cookie: cookie2, consentTypes, snapshot: {} });
            } catch {
                /* empty */
            } finally {
                rootState.waiting = false;
            }
        },
        async loadProject({ rootState, commit }, uuid) {
            rootState.waiting = true;
            try {
                const { project, snapshot } = await projectsService.getOneWithSnapshot({
                    tool: "cookie",
                    uuid,
                });
                const cookie = prepareCookie(project);
                const { consentTypes } = cookie;
                commit("setProject", { cookie, consentTypes, snapshot });
            } catch {
                /* empty */
            } finally {
                rootState.waiting = false;
            }
        },
        generateScript2({ state, rootState }) {
            const functionModule = require("@/apps/cookie/config/cookie-new.js");
            let functionText = functionModule.toString();

            const iframeDocument = state.iframe.contentDocument;
            const cookieHtml = JSON.stringify(
                iframeDocument.getElementById(`view-${state.cookie.type}`).outerHTML
            );
            const adjustHtml = JSON.stringify(iframeDocument.getElementById(`adjust`).outerHTML);
            functionText = functionText.replace("fontNameToReplace", state.cookie.fontFamily);
            functionText = functionText.replace('"cookieHtmlToReplace"', cookieHtml);
            functionText = functionText.replace(
                '"adjustHtmlToReplace"',
                state.cookie.adjustShow ? adjustHtml : "''"
            );
            functionText = functionText.replace("webhookUrlToReplace", state.cookie.webhookUrl);
            functionText = functionText.replace("cookieIdToReplace", state.cookie.id);
            functionText = functionText.replace("originToReplace", rootState.globalUrl);
            functionText = functionText.replace("clientUrlToReplace", rootState.globalUrl);
            functionText = functionText.replace(
                "listOfConsentsToReplace",
                state.cookie.listWithConsents ? true : false
            );
            functionText = functionText.replace("sendToGtmToReplace", state.cookie.sendToGtm);

            functionText = `(${functionText})();`;
            return functionText;
        },
        publishProject2({ rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            dispatch("hostCode2");
        },
        async unpublishCookie({ state, commit, dispatch }) {
            await dispatch("unpublish", { uuid: state.cookie.uuid });
            commit("setUnpublished", state.cookie, { root: true });
            dispatch("mergeSnapshotChanges");
        },
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        async unpublish({ _ }, { uuid }) {
            return cookiesService.publish({ uuid, code: "", publish: false });
        },
        showCodeModal({ state, rootState, commit }) {
            const scriptLink = `<script src="${rootState.globalUrl}/cookies/scripts/${state.cookie.uuid}.js" type="text/javascript"></script>`;
            const modalData = {
                type: "code",
                title: t("cookie.publish_modal.title"),
                desc: t("cookie.publish_modal.description"),
                code: scriptLink,
                actionBtn: t("cookie.publish_modal.copy_script"),
                cancelBtn: t("shared.close"),
                callback: () => {
                    copy(scriptLink);
                    commit(
                        "showAlert",
                        {
                            type: "success",
                            title: t("cookie.publish_modal.script_copied"),
                        },
                        { root: true }
                    );
                },
            };
            commit("showModal", modalData, { root: true });
        },
        async hostCode2({ state, rootState, rootGetters, getters, commit, dispatch }) {
            rootState.waiting = true;

            state.iframe.contentWindow.postMessage({ showPreview: "off" }, getters.appUrl);

            await waitForMe(100);
            await nextTick();

            const firstPublish = !rootGetters.isPublished(state.cookie);

            const scriptText = await dispatch("generateScript2");

            try {
                await cookiesService.publish({
                    uuid: state.cookie.uuid,
                    code: parseHtml(scriptText),
                    publish: true,
                });

                state.cookie.code = parseHtml(scriptText);
                commit("setPublished", state.cookie, { root: true });
                dispatch("mergeSnapshotChanges");
                if (firstPublish) {
                    dispatch("showCodeModal");
                }
            } finally {
                rootState.waiting = false;
                state.iframe.contentWindow.postMessage({ showPreview: "on" }, getters.appUrl);
            }
        },
        changeFontFamily({ state }, payload) {
            state.cookie.fontFamily = payload;
        },
        async createListOfConsents({ state, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }
            state.cookie.listWithConsents = !state.cookie.listWithConsents;
            dispatch("mergeSnapshotChanges");
        },
    },
};
